"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const joi_1 = __importDefault(require("joi"));
const errors_1 = require("../../errors");
const IInvoice_1 = require("../models/invoice/IInvoice");
const TransactionType_1 = require("../models/rates/TransactionType");
const InvoiceNumberSettingsValidator_1 = __importDefault(require("./InvoiceNumberSettingsValidator"));
const notificationConfiguration_schema_1 = __importDefault(require("./schemas/notificationConfiguration.schema"));
class CustomerSettingsValidator {
    constructor(invoiceNumberSetingsValidator) {
        this._invoiceNumberSetingsValidator =
            invoiceNumberSetingsValidator ??
                new InvoiceNumberSettingsValidator_1.default();
    }
    _invoiceNumberSetingsValidator;
    transactionTypes = Object.values(TransactionType_1.TransactionType);
    _addressSchema = joi_1.default.object({
        addr1: joi_1.default.string().allow('').required(),
        addr2: joi_1.default.string().allow('').required(),
        city: joi_1.default.string().allow('').required(),
        state: joi_1.default.string().allow('').required(),
        zip: joi_1.default.string().allow('').required(),
        country: joi_1.default.string().allow('').required(),
        contactName: joi_1.default.string().allow('').optional(),
        contactPhone: joi_1.default.string().allow('').optional(),
        contactEmail: joi_1.default.string().allow('').optional(),
    });
    _categoryOverridesSchema = joi_1.default.object({
        category: joi_1.default.string().required(),
        glAccount: joi_1.default.string().required(),
    });
    _transactionOverridesSchema = joi_1.default.object({
        transactionType: joi_1.default.string()
            .valid(...this.transactionTypes)
            .required(),
        glAccount: joi_1.default.string().required(),
        categoryOverrides: joi_1.default.array()
            .items(this._categoryOverridesSchema)
            .required(),
    });
    _glAccountSettingsSchema = joi_1.default.object({
        defaultGlAccount: joi_1.default.string().allow('').optional(),
        transactionOverrides: joi_1.default.array()
            .items(this._transactionOverridesSchema)
            .required(),
    });
    _autoInvoiceSettings = joi_1.default.object({
        frequency: joi_1.default.string()
            .valid(...Object.values(IInvoice_1.InvoiceFrequency))
            .required(),
        lastInvoice: joi_1.default.number().optional(),
        nextInvoice: joi_1.default.number().required(),
        startsOn: joi_1.default.number().optional(),
        invoiceBy: joi_1.default.string()
            .valid(...Object.values(IInvoice_1.IInvoicedBy))
            .required(),
        autoPublish: joi_1.default.bool().optional(),
    });
    _convenienceFeesSettingsSchema = joi_1.default.object({
        CCFeeEnabled: joi_1.default.bool().required(),
        ACHFeeEnabled: joi_1.default.bool().required(),
    });
    _warehousesSchema = joi_1.default.array()
        .items(joi_1.default.object({
        name: joi_1.default.string().required(),
        id: joi_1.default.string().required(),
        measurementStandard: joi_1.default.string().optional(),
        timezone: joi_1.default.string().optional(),
    }))
        .optional();
    _schema = joi_1.default.object({
        id: joi_1.default.string().allow('').required(),
        orgKey: joi_1.default.string().required(),
        customerId: joi_1.default.string().required(),
        customerName: joi_1.default.string().allow('').required(),
        warehouses: this._warehousesSchema,
        settings: joi_1.default.object({
            glAccountSettings: this._glAccountSettingsSchema.required(),
            primaryAddress: this._addressSchema.required(),
            secondaryAddress: this._addressSchema,
            payrixCustomerId: joi_1.default.string().allow('').optional(),
            invoiceNumberSettings: joi_1.default.object().optional(),
            allowCCPayment: joi_1.default.boolean().required(),
            allowACHPayment: joi_1.default.boolean().required(),
            paymentTerm: joi_1.default.number().integer().positive().max(365).required(),
            autoInvoice: joi_1.default.boolean().required(),
            autoInvoiceSettings: joi_1.default.when('autoInvoice', {
                is: true,
                then: this._autoInvoiceSettings.required(),
                otherwise: this._autoInvoiceSettings.optional(),
            }),
            convenienceFeesSettings: this._convenienceFeesSettingsSchema.optional(),
            notificationSettings: joi_1.default.array()
                .items(notificationConfiguration_schema_1.default)
                .optional(),
        }).required(),
    }).required();
    validate(customerSettings) {
        const issues = [];
        const result = this._schema.validate(customerSettings, {
            abortEarly: false,
            convert: false,
        });
        if (result.error) {
            issues.push(...this.handleValidationError(result.error));
        }
        const invoiceNumberSettings = customerSettings?.settings?.invoiceNumberSettings;
        if (invoiceNumberSettings) {
            try {
                this._invoiceNumberSetingsValidator.validate(invoiceNumberSettings);
            }
            catch (error) {
                if (error instanceof errors_1.ValidationError) {
                    issues.push(...error.validationIssues);
                }
                else {
                    issues.push(new errors_1.Errors.ValidationIssue('ICustomerSettings', errors_1.Errors.ValidationErrorCode.invalidValue, 'invoiceNumberSettings', invoiceNumberSettings));
                }
            }
        }
        if (issues.length > 0) {
            const validationError = new errors_1.Errors.ValidationError();
            issues.forEach((issue) => {
                validationError.addIssue(issue.modelType, issue.errorCode, issue.fieldName, issue.value, issue.path);
            });
            throw validationError;
        }
    }
    handleValidationError(error) {
        const issues = [];
        error.details.forEach((issue) => {
            switch (issue.type) {
                case 'any.required': {
                    issues.push(new errors_1.Errors.ValidationIssue('ICustomerSettings', errors_1.Errors.ValidationErrorCode.requiredValue, issue.context?.key, undefined));
                    break;
                }
                case 'object.unknown': {
                    issues.push(new errors_1.Errors.ValidationIssue('ICustomerSettings', errors_1.Errors.ValidationErrorCode.notAllowed, issue.context?.key, issue.context?.value));
                    break;
                }
                case 'object.base':
                case 'string.base':
                case 'array.base':
                case 'boolean.base':
                case 'number.base': {
                    issues.push(new errors_1.Errors.ValidationIssue('ICustomerSettings', errors_1.Errors.ValidationErrorCode.invalidType, issue.context?.label, issue.context?.value));
                    break;
                }
                case 'any.only':
                case 'array.min':
                case 'array.max':
                case 'number.greater':
                case 'number.integer':
                case 'number.less':
                case 'number.max':
                case 'number.min':
                case 'number.negative':
                case 'number.positive':
                case 'number.precision':
                case 'string.alphanum':
                case 'string.case':
                case 'string.empty':
                case 'string.guid':
                case 'string.length':
                case 'string.lowercase':
                case 'string.max':
                case 'string.min':
                case 'string.trim':
                case 'string.uppercase':
                case 'string.pattern.base': {
                    issues.push(new errors_1.Errors.ValidationIssue('ICustomerSettings', errors_1.Errors.ValidationErrorCode.invalidValue, issue.context?.label, issue.context?.value));
                    break;
                }
            }
        });
        return issues;
    }
}
exports.default = CustomerSettingsValidator;
